import * as React from 'react';
import { Box, Tooltip, Fade, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/sharp-regular-svg-icons';

/**
 * Convertit une couleur (hex, rgb, rgba, nom) vers rgba avec l'opacité spécifiée
 */
const convertToRgba = (color: string, opacity: number): string => {
    // En environnement de test, retourner une valeur par défaut
    if (typeof document === 'undefined' || !document.createElement) {
        return `rgba(128, 128, 128, ${opacity})`;
    }

    // Créer un élément temporaire pour utiliser les capacités du navigateur
    const tempElement = document.createElement('div');
    tempElement.style.color = color;
    document.body.appendChild(tempElement);

    const computedColor = window.getComputedStyle(tempElement).color;
    document.body.removeChild(tempElement);

    // Extraire les valeurs RGB depuis rgb() ou rgba()
    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    const rgbaMatch = computedColor.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)/);

    if (rgbaMatch) {
        const [, r, g, b] = rgbaMatch;
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    if (rgbMatch) {
        const [, r, g, b] = rgbMatch;
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Fallback si la conversion échoue
    return color;
};

export type SectionOverlayColors = {
    /**
     * Couleur de fond de l'overlay (par défaut: '#f0f0f0')
     */
    overlayBackground?: string;
    /**
     * Couleur de fond du cercle du cadenas (par défaut: '#ffffff')
     */
    lockIconBackground?: string;
    /**
     * Couleur de l'icône de cadenas (par défaut: '#9e9e9e')
     */
    lockIconColor?: string;
    /**
     * Couleur de fond du tooltip (par défaut: '#212121')
     */
    tooltipBackground?: string;
    /**
     * Couleur du texte du tooltip (par défaut: '#ffffff')
     */
    tooltipTextColor?: string;
    /**
     * Couleur de l'ombre du cercle du cadenas (par défaut: '#000000')
     */
    lockIconShadow?: string;
};

export type SectionOverlayOpacities = {
    /**
     * Opacité de fond de l'overlay (par défaut: 0.7)
     */
    overlayBackground?: number;
    /**
     * Opacité de fond du cercle du cadenas (par défaut: 0.9)
     */
    lockIconBackground?: number;
    /**
     * Opacité de l'icône de cadenas (par défaut: 1)
     */
    lockIconColor?: number;
    /**
     * Opacité de fond du tooltip (par défaut: 0.95)
     */
    tooltipBackground?: number;
    /**
     * Opacité du texte du tooltip (par défaut: 1)
     */
    tooltipTextColor?: number;
    /**
     * Opacité de l'ombre du cercle du cadenas (par défaut: 0.1)
     */
    lockIconShadow?: number;
};

export type SectionOverlayProps = {
    /**
     * Identifiant unique de la section pour le calcul des dimensions
     */
    sectionId: string;
    /**
     * Indique si l'overlay doit être affiché
     */
    isLocked: boolean;
    /**
     * Message explicatif affiché au hover du cadenas
     */
    lockMessage: string;
    /**
     * Z-index de l'overlay (par défaut 10)
     */
    zIndex?: number;
    /**
     * Délai avant le calcul des dimensions en ms (par défaut 200)
     */
    calculationDelay?: number;
    /**
     * Personnalisation des couleurs
     */
    colors?: SectionOverlayColors;
    /**
     * Personnalisation des opacités
     */
    opacities?: SectionOverlayOpacities;
    /**
     * Rayon de bordure de l'overlay (par défaut: '8px')
     */
    borderRadius?: string | number;
    /**
     * Intensité du flou de l'overlay (par défaut: '2px')
     */
    blurIntensity?: string;
};

export type OverlayDimensions = {
    height: number;
    top: number;
    left: number;
    width: string;
};

const DEFAULT_COLORS: Required<SectionOverlayColors> = {
    overlayBackground: '#f0f0f0',
    lockIconBackground: '#ffffff',
    lockIconColor: '#9e9e9e',
    tooltipBackground: '#212121',
    tooltipTextColor: '#ffffff',
    lockIconShadow: '#000000',
};

const DEFAULT_OPACITIES: Required<SectionOverlayOpacities> = {
    overlayBackground: 0.7,
    lockIconBackground: 0.9,
    lockIconColor: 1,
    tooltipBackground: 0.95,
    tooltipTextColor: 1,
    lockIconShadow: 0.1,
};

export const SectionOverlay: React.FunctionComponent<SectionOverlayProps> = function ({
    sectionId,
    isLocked,
    lockMessage,
    zIndex = 10,
    calculationDelay = 200,
    colors = {},
    opacities = {},
    borderRadius = '8px',
    blurIntensity = '2px',
}) {
    const markerRef = React.useRef<HTMLDivElement>(null);
    const [overlayDimensions, setOverlayDimensions] = React.useState<OverlayDimensions>({
        height: 260,
        top: 20,
        left: -12,
        width: '103%',
    });
    const [isHovered, setIsHovered] = React.useState(false);

    // Merger les couleurs et opacités par défaut avec les personnalisées
    const finalColors = React.useMemo(
        () => ({
            ...DEFAULT_COLORS,
            ...colors,
        }),
        [colors],
    );

    const finalOpacities = React.useMemo(
        () => ({
            ...DEFAULT_OPACITIES,
            ...opacities,
        }),
        [opacities],
    );

    // Créer les couleurs finales avec opacité
    const finalColorsWithOpacity = React.useMemo(
        () => ({
            overlayBackground: convertToRgba(
                finalColors.overlayBackground,
                finalOpacities.overlayBackground,
            ),
            lockIconBackground: convertToRgba(
                finalColors.lockIconBackground,
                finalOpacities.lockIconBackground,
            ),
            lockIconColor: convertToRgba(finalColors.lockIconColor, finalOpacities.lockIconColor),
            tooltipBackground: convertToRgba(
                finalColors.tooltipBackground,
                finalOpacities.tooltipBackground,
            ),
            tooltipTextColor: convertToRgba(
                finalColors.tooltipTextColor,
                finalOpacities.tooltipTextColor,
            ),
            lockIconShadow: convertToRgba(
                finalColors.lockIconShadow,
                finalOpacities.lockIconShadow,
            ),
        }),
        [finalColors, finalOpacities],
    );

    const calculateSectionDimensions = React.useCallback(() => {
        if (!markerRef.current) return;

        const sectionElements = document.querySelectorAll(`.section-${sectionId}-field`);
        const markerRect = markerRef.current.getBoundingClientRect();

        let minTop = Infinity;
        let maxBottom = -Infinity;
        let minLeft = Infinity;
        let maxRight = -Infinity;

        sectionElements.forEach((element) => {
            const rect = element.getBoundingClientRect();
            if (rect.height > 0) {
                const relativeTop = rect.top - markerRect.top;
                const relativeBottom = rect.bottom - markerRect.top;
                const relativeLeft = rect.left - markerRect.left;
                const relativeRight = rect.right - markerRect.left;

                minTop = Math.min(minTop, relativeTop);
                maxBottom = Math.max(maxBottom, relativeBottom);
                minLeft = Math.min(minLeft, relativeLeft);
                maxRight = Math.max(maxRight, relativeRight);
            }
        });

        if (minTop !== Infinity) {
            setOverlayDimensions({
                height: maxBottom - minTop + 20,
                top: minTop - 10,
                left: minLeft - 10,
                width: `${maxRight - minLeft + 20}px`,
            });
        }
    }, [sectionId]);

    React.useEffect(() => {
        if (!markerRef.current || !isLocked) return undefined;

        const timer = setTimeout(calculateSectionDimensions, calculationDelay);
        const resizeTimer = setTimeout(() => {
            window.addEventListener('resize', calculateSectionDimensions);
        }, calculationDelay + 100);

        return () => {
            clearTimeout(timer);
            clearTimeout(resizeTimer);
            window.removeEventListener('resize', calculateSectionDimensions);
        };
    }, [isLocked, calculateSectionDimensions, calculationDelay]);

    const handleInteractionBlock = React.useCallback(
        (event: React.MouseEvent | React.KeyboardEvent) => {
            event.preventDefault();
            event.stopPropagation();
        },
        [],
    );

    if (!isLocked) return null;

    return (
        <Box
            ref={markerRef}
            data-testid="section-overlay-container"
            data-section-id={sectionId}
            sx={{
                position: 'relative',
                height: 0,
            }}
        >
            {/* Overlay de fond avec blocage des interactions */}
            <Box
                data-testid="section-overlay-background"
                sx={{
                    position: 'absolute',
                    top: `${overlayDimensions.top}px`,
                    left: `${overlayDimensions.left}px`,
                    height: `${overlayDimensions.height}px`,
                    width: overlayDimensions.width,
                    backgroundColor: finalColorsWithOpacity.overlayBackground,
                    backdropFilter: `blur(${blurIntensity})`,
                    zIndex,
                    borderRadius,
                    pointerEvents: 'auto',
                }}
                onClick={handleInteractionBlock}
                onMouseDown={handleInteractionBlock}
                onKeyDown={handleInteractionBlock}
            />

            {/* Icône de verrouillage avec tooltip */}
            <Tooltip
                title={
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 'medium',
                                mb: 0.5,
                                color: finalColorsWithOpacity.tooltipTextColor,
                            }}
                        >
                            Section verrouillée
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                opacity: 0.9,
                                lineHeight: 1.3,
                                color: finalColorsWithOpacity.tooltipTextColor,
                            }}
                        >
                            {lockMessage}
                        </Typography>
                    </Box>
                }
                placement="bottom"
                arrow
                open={isHovered}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 300 }}
                componentsProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: finalColorsWithOpacity.tooltipBackground,
                            maxWidth: '280px',
                        },
                    },
                    arrow: {
                        sx: {
                            color: finalColorsWithOpacity.tooltipBackground,
                        },
                    },
                }}
            >
                <Box
                    data-testid="section-overlay-lock-icon"
                    sx={{
                        position: 'absolute',
                        top: `${overlayDimensions.top + overlayDimensions.height / 2 - 30}px`,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: zIndex + 1,
                        width: 60,
                        height: 60,
                        backgroundColor: finalColorsWithOpacity.lockIconBackground,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 8px ${finalColorsWithOpacity.lockIconShadow}`,
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                            transform: 'translateX(-50%) scale(1.1)',
                        },
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <FontAwesomeIcon
                        icon={faLock}
                        style={{
                            color: finalColorsWithOpacity.lockIconColor,
                            fontSize: '1.5rem',
                        }}
                    />
                </Box>
            </Tooltip>
        </Box>
    );
};
